import { datadogRum } from '@datadog/browser-rum'
import isNil from 'lodash/isNil'
import { enqueueSnackbar } from 'notistack'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { DocumentsHttpClient } from '@omnea-app/documents/documentsHttpClient'
import { useToken } from '@omnea-app/fe-auth/TokenProvider'
import { BlobUrlHandle } from '@omnea-app/fe-browser-utils/BlobUrlHandle'
import { onFetchFailErrors } from '@omnea-app/fe-legacy/utils/error-handling/fetchFailHandling'
import { isNotFoundApiError } from '@omnea-app/packages/universal/error-handling/isApiErrorOfType'
import { ApiError } from '@omnea-app/packages/universal/error-handling/throwifApiError'
import { getUploadFailureMessage } from './getUploadFailureMessage'
import { recoverFileExtension } from './recoverFileExtension'
import { S3UploadFailure, uploadFilesToS3 } from './uploadFilesToS3'

/**
 * Rejects URL-shaped text so a failure message quoting the presigned URL cannot carry its path or query
 * string through. `:` is permitted so Chrome's `net::ERR_*` suffix survives — on its own it cannot form a
 * URL, since `/ ? & = %` stay excluded. It does not make a message safe in general: a credential value is
 * plain `\w` and would pass. Only the browser-authored messages on `network` and `unknown` reach here.
 */
const SAFE_TELEMETRY_MESSAGE = /^[\w .,:'()-]{1,200}$/

const toSafeMessage = (message: string): string => (SAFE_TELEMETRY_MESSAGE.test(message) ? message : 'redacted')

/** Nothing derived from the filename is reported. */
const toFailureTelemetry = (failures: S3UploadFailure[]) =>
  failures.map((failure) => ({
    kind: failure.kind,
    fileSizeBytes: failure.fileSizeBytes,
    ...(failure.kind === 'http' ? { status: failure.status } : {}),
    ...(failure.kind === 'timeout' ? { timeoutMs: failure.timeoutMs } : {}),
    ...('message' in failure ? { message: toSafeMessage(failure.message) } : {}),
  }))

const DocTypesViewableInBrowser = ['application/pdf', 'image/*']
const isDocTypeViewableInBrowser = (contentType: string) => {
  return (
    DocTypesViewableInBrowser.includes(contentType) ||
    DocTypesViewableInBrowser.some((type) => type.endsWith('/*') && contentType.startsWith(type.slice(0, -1)))
  )
}

export const useDocuments = () => {
  const { getToken } = useToken()
  const { t } = useTranslation('documents')
  const [isUploadingDocument, setIsUploadingDocument] = useState(false)
  const [uploadError, setUploadError] = useState<string | null>(null)

  const clearUploadError = () => setUploadError(null)

  const uploadDocument = async (documents: File[]) => {
    setIsUploadingDocument(true)
    setUploadError(null)

    const documentsToUpload = documents.filter((document) => document.size > 0)
    if (documentsToUpload.length !== documents.length) {
      enqueueSnackbar(t('upload-document-warning', { documentCount: documents.length - documentsToUpload.length }), {
        variant: 'warning',
      })
    }

    try {
      const token = await getToken()
      const { uploaded, failedFileIndices, failures } = await uploadFilesToS3(documentsToUpload, token)

      if (failedFileIndices.size > 0) {
        const failureMessage = getUploadFailureMessage(failures, t)
        setUploadError(failureMessage)
        enqueueSnackbar(failureMessage, { variant: 'error' })
        datadogRum.addError(new Error('S3 upload failed for one or more files'), {
          messages: ['Document Upload to S3'],
          failures: toFailureTelemetry(failures),
        })
        return null
      }

      return uploaded
    } catch (error) {
      setUploadError(t('upload-document-error'))
      enqueueSnackbar(t('upload-document-error'), { variant: 'error' })
      datadogRum.addError(error, { messages: ['Document Upload to S3'] })
      return null
    } finally {
      setIsUploadingDocument(false)
    }
  }

  const getBlobUrlHandle = async (
    url: string
  ): Promise<{ blobUrlHandle: BlobUrlHandle; isViewableInBrowser: boolean; contentType: string }> => {
    const response = await fetch(url)
    const contentType = response.headers.get('Content-Type') ?? ''
    const blob = await response.blob()
    const isViewableInBrowser = isDocTypeViewableInBrowser(contentType)

    return { blobUrlHandle: new BlobUrlHandle(blob), isViewableInBrowser, contentType }
  }

  const downloadDoc = async ({
    fileName,
    isViewableInBrowser,
    blobUrlHandle,
    forceDownload = false,
  }: {
    fileName: string
    isViewableInBrowser: boolean
    blobUrlHandle: BlobUrlHandle
    forceDownload?: boolean
  }) => {
    const link = document.createElement('a')
    if (isViewableInBrowser && !forceDownload) {
      if (!blobUrlHandle.url) return
      link.href = blobUrlHandle.url
      link.target = '_blank' // For preview
      link.rel = 'noopener noreferrer'
    } else {
      link.href = blobUrlHandle.url
    }

    link.download = fileName
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)

    blobUrlHandle.revoke()
  }

  const isForbiddenApiError = (error: unknown): error is { type: 'ForbiddenError'; detail: object } => {
    return !isNil(error) && typeof error === 'object' && 'type' in error && error.type === 'ForbiddenError'
  }

  const fetchSignedUrl = async (filestoreId: string, documentId?: string) => {
    try {
      const token = await getToken()
      const documentsClient = new DocumentsHttpClient({ authorization: token })
      return await documentsClient.GET('/signed-url', {
        params: {
          query: {
            filestoreId,
            id: documentId,
          },
        },
      })
    } catch (error) {
      if (isNotFoundApiError(error)) {
        return {
          data: null,
          error: { type: 'NotFoundError' as const, detail: error.detail },
        }
      }
      throw error
    }
  }

  const getSignedUrl = async ({
    filestoreId,
    fileName,
    documentId,
  }: {
    filestoreId: string
    fileName: string
    documentId?: string
  }) => {
    try {
      const { data: documentDownloadResponse, error } = await fetchSignedUrl(filestoreId, documentId)

      if (error?.type === 'ForbiddenError') {
        throw new ApiError(error)
      }

      if (error?.type === 'NotFoundError') {
        return { notFound: true }
      }

      if (!documentDownloadResponse) {
        console.error('No response when downloading document', { error, filestoreId, fileName })
        return null
      }

      if (documentDownloadResponse.validationStatus === 'PENDING') {
        return null
      }

      return documentDownloadResponse.signedUrl
    } catch (error) {
      onFetchFailErrors('Downloading document failed', error, { filestoreId, fileName })
      // Re-throw ForbiddenError so polling hook can handle it
      if (error instanceof Error) {
        throw error
      }
      // Then check if it's a raw API error that needs wrapping
      if (isForbiddenApiError(error)) {
        throw new ApiError(error)
      }
      return null
    }
  }

  const downloadDocument = async ({
    filestoreId,
    fileName,
    mode,
    documentId,
  }: {
    filestoreId: string
    fileName: string
    mode?: 'preview' | 'downloadFile'
    documentId?: string
  }): Promise<
    | { type: 'preview'; url: string }
    | { type: 'download' }
    | { type: 'pending'; message: string }
    | { type: 'quarantined'; message: string }
    | { type: 'notFound'; message: string }
    | null
  > => {
    try {
      const { data: documentDownloadResponse, error } = await fetchSignedUrl(filestoreId, documentId)

      if (error?.type === 'ForbiddenError') {
        return {
          type: 'quarantined',
          message: t('file.unavailable'),
        }
      }

      if (error?.type === 'NotFoundError') {
        return {
          type: 'notFound',
          message: t('file.not-found'),
        }
      }

      if (!documentDownloadResponse) {
        console.error('No response when downloading document', { error, filestoreId, fileName })
        return null
      }

      if (documentDownloadResponse.validationStatus === 'PENDING') {
        return {
          type: 'pending',
          message: documentDownloadResponse.message,
        }
      }

      const s3SignedUrl = documentDownloadResponse.signedUrl

      const { blobUrlHandle, isViewableInBrowser, contentType } = await getBlobUrlHandle(s3SignedUrl)
      if (mode !== 'preview' || !isViewableInBrowser) {
        const { fileName: downloadFileName, hadKnownExtension } = recoverFileExtension({ fileName, contentType })
        if (!hadKnownExtension) {
          datadogRum.addAction('documentDownloadExtensionRecovery', {
            contentType,
            recovered: downloadFileName !== fileName,
          })
        }
        await downloadDoc({
          fileName: downloadFileName,
          isViewableInBrowser,
          blobUrlHandle,
          forceDownload: mode === 'downloadFile',
        })
        return { type: 'download' }
      } else {
        return { type: 'preview', url: s3SignedUrl }
      }
    } catch (error) {
      onFetchFailErrors('Downloading document failed', error, { filestoreId, fileName })
      return null
    }
  }

  return { uploadDocument, isUploadingDocument, uploadError, clearUploadError, getSignedUrl, downloadDocument }
}
